import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAppState } from "contexts/UserContext";
import { useAuth } from "contexts/AuthContext";
import { useTranslation } from "react-i18next";
import normalizeDateString from "utils/common/normalizeDateString";
import useCauseProcessor from "../../../hooks/useCauseProcessor";

const useMoodSelection = ({ onMoodSelected } = {}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { moods } = useAppState();
  const { userId } = useAuth();
  const [selectingMood, setSelectingMood] = useState(null);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [moodAlreadySelectedToday, setMoodAlreadySelectedToday] =
    useState(false);

  const { getCausesForMood, enrichCausesWithTranslations } =
    useCauseProcessor();

  const handleMoodClick = useCallback(
    async (
      index,
      moodGenitiveLabels,
      feelingNames,
      selectedDate,
      moodImages,
      relevantMoods
    ) => {
      try {
        setIsFadingOut(true);

        const moodKey = moodGenitiveLabels[index]?.toLowerCase();
        const moodGenitive = t(`moodsGenitive.${moodKey}`) || moodKey;
        const moodInEnglish = feelingNames[index];
        const selectedDateNormalized = normalizeDateString(selectedDate);

        // Find existing mood entry
        const existingEntry = moods.find(
          (m) =>
            m.uid === userId &&
            m.mood === moodInEnglish &&
            normalizeDateString(m.timestamp) === selectedDateNormalized
        );

        // Get causes for this mood
        const filteredSpecs = existingEntry
          ? getCausesForMood(existingEntry.id)
          : [];

        // Enrich causes with translations (use all cause types for existing saved causes)
        const filteredSpecsWithTranslation = enrichCausesWithTranslations(
          filteredSpecs,
          true // useAllCauseTypes = true for existing saved causes
        );

        setMoodAlreadySelectedToday(!!existingEntry);

        const statePayload = {
          moodName: moodGenitive,
          imageIndex: index,
          feelingName: moodInEnglish,
          selectedDate: selectedDate,
          imageSrc: moodImages[index]?.src,
          enableDelete: existingEntry,
          existingRecord: existingEntry ?? null,
          selectedMood: relevantMoods[index],
          savedCauses: filteredSpecsWithTranslation,
          mode: existingEntry ? "existing" : "new",
        };

        // Navigate or open drawer after fade-out transition
        setTimeout(() => {
          if (onMoodSelected) {
            onMoodSelected(statePayload);
            return;
          }

          navigate("/app/checkin", { state: statePayload });
        }, 300);

        // Prevent duplicate selections
        if (selectingMood === index) return;
        setSelectingMood(index);
      } catch (err) {
        console.error("[handleMoodClick] Error:", err);
        setIsFadingOut(false); // Reset fade state on error
      }
    },
    [
      navigate,
      t,
      moods,
      userId,
      selectingMood,
      getCausesForMood,
      enrichCausesWithTranslations,
      onMoodSelected,
    ]
  );

  const resetSelection = useCallback(() => {
    setSelectingMood(null);
    setIsFadingOut(false);
  }, []);

  return {
    selectingMood,
    isFadingOut,
    moodAlreadySelectedToday,
    handleMoodClick,
    resetSelection,
  };
};

export default useMoodSelection;
